import {
    ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    MagnifyingGlassIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import type { DataTableExpansion, DataTableSearchConfig } from './types';

/**
 * Toolbar slot that sits above the table card: search on the left, filters
 * and actions on the right. Purely layout - it owns no state and imposes no
 * controls, so filter dropdowns, "Refreshing…" hints and action buttons all
 * compose in as `end` content.
 */
export interface DataTableToolbarProps {
    start?: ReactNode;
    end?: ReactNode;
    className?: string;
}

export function DataTableToolbar({ start, end, className }: DataTableToolbarProps) {
    return (
        <div className={`flex items-center justify-between gap-4 py-4 shrink-0 ${className ?? ''}`}>
            <div className="flex items-center gap-2.5 min-w-0">{start}</div>
            <div className="flex items-center gap-2.5 shrink-0">{end}</div>
        </div>
    );
}

/**
 * Search input matching the design system's search bar. Controlled and
 * stateless - debouncing and querying stay with the caller (see
 * `useDebounce`), because search is a server-side concern here.
 */
export type DataTableSearchProps = DataTableSearchConfig;

export function DataTableSearch({
    value,
    onChange,
    placeholder,
    label,
    maxLength,
    className
}: DataTableSearchProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div
            className={`flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4 shrink-0 ${className ?? ''}`}
        >
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 shrink-0" aria-hidden />
            {/* WebKit's own cancel pseudo-element is suppressed: it exists in
                Chrome only, cannot be focused or labelled, and would sit next
                to the explicit clear button below as a second, different "×".
                One affordance, identical in every browser, wins. */}
            <input
                ref={inputRef}
                type="search"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                aria-label={label ?? placeholder ?? 'Search'}
                className="bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 w-full [&::-webkit-search-cancel-button]:appearance-none"
            />
            {/* The explicit clear affordance the pre-migration toolbars had.
                Native `type="search"` clearing is Chrome-only, so without
                this button Firefox offers no way to clear but select-all +
                delete. Clearing hands focus back to the input: the button
                unmounts with the value, and focus must not fall to <body>. */}
            {value !== '' && (
                <button
                    type="button"
                    onClick={() => {
                        onChange('');
                        inputRef.current?.focus();
                    }}
                    aria-label="Clear search"
                    className="ml-2 shrink-0 rounded text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                >
                    <XMarkIcon className="w-4 h-4" aria-hidden />
                </button>
            )}
        </div>
    );
}

/**
 * Expand / collapse toggle for the full-screen overlay. Drive it from
 * `useDataTableExpansion` and pass the same object to `<DataTable expansion>`.
 */
export interface DataTableExpandToggleProps {
    expansion: DataTableExpansion;
    /** Names the thing being expanded, e.g. "invoices table". */
    label?: string;
    className?: string;
}

export function DataTableExpandToggle({ expansion, label, className }: DataTableExpandToggleProps) {
    const what = label ?? 'table';
    const Icon = expansion.expanded ? ArrowsPointingInIcon : ArrowsPointingOutIcon;
    return (
        <button
            ref={expansion.triggerRef}
            type="button"
            onClick={expansion.toggle}
            aria-expanded={expansion.expanded}
            aria-label={expansion.expanded ? `Collapse ${what}` : `Expand ${what}`}
            title={expansion.expanded ? 'Collapse (Esc)' : 'Expand'}
            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${className ?? ''}`}
        >
            <Icon className="w-4 h-4" aria-hidden />
        </button>
    );
}
